import { useCallback } from 'react';
import { Box, Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { IconArrowLeft, IconMaximize } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { GAMES_BY_SLUG } from '../data/games';
import { useDos } from '../hooks/useDos';

export function PlayerView() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const game = slug ? GAMES_BY_SLUG[slug] : undefined;

  if (!game) {
    return (
      <Center mih="60vh">
        <Stack align="center" gap="sm">
          <Text>Game not found.</Text>
          <Button variant="light" onClick={() => navigate('/arcade')}>
            Back to Arcade
          </Button>
        </Stack>
      </Center>
    );
  }

  if (!game.available) {
    return (
      <Center mih="60vh">
        <Stack align="center" gap="sm">
          <Text fw={600}>{game.title}</Text>
          <Text c="dimmed" size="sm">
            This episode isn't bundled yet. Drop the bundle at
            <Text span ff="monospace" mx={4}>
              web/public{game.bundleUrl}
            </Text>
            and refresh.
          </Text>
          <Button variant="light" onClick={() => navigate('/arcade')}>
            Back to Arcade
          </Button>
        </Stack>
      </Center>
    );
  }

  return <PlayerCanvas slug={game.slug} bundleUrl={game.bundleUrl} title={game.title} />;
}

function PlayerCanvas({
  bundleUrl,
  title,
}: {
  slug: string;
  bundleUrl: string;
  title: string;
}) {
  const navigate = useNavigate();
  const { containerRef, status, error } = useDos(bundleUrl);

  const goFullscreen = useCallback(() => {
    const el = containerRef.current?.parentElement;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen?.();
    }
  }, [containerRef]);

  return (
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        background: '#000',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Group
        gap="xs"
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 1001,
        }}
      >
        <Button
          size="xs"
          variant="filled"
          color="dark.6"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/arcade')}
        >
          Arcade
        </Button>
        <Button
          size="xs"
          variant="filled"
          color="dark.6"
          leftSection={<IconMaximize size={14} />}
          onClick={goFullscreen}
        >
          Fullscreen
        </Button>
      </Group>
      <Box ref={containerRef} style={{ flex: 1, minHeight: 0 }} />
      {status === 'loading' && (
        <Center
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
          <Stack align="center" gap="xs">
            <Loader color="gray.5" />
            <Text c="gray.5" size="sm">
              Booting {title}…
            </Text>
          </Stack>
        </Center>
      )}
      {status === 'error' && (
        <Center style={{ position: 'absolute', inset: 0 }}>
          <Stack align="center" gap="sm">
            <Text c="red.4">Failed to start: {error}</Text>
            <Button variant="light" onClick={() => navigate('/arcade')}>
              Back to Arcade
            </Button>
          </Stack>
        </Center>
      )}
    </Box>
  );
}
